import React, { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

/**
 * Floating "Install app" affordance.
 * - Chrome/Android/desktop: captures beforeinstallprompt and shows a one-tap install button.
 * - iOS Safari (no prompt API): shows Add-to-Home-Screen instructions.
 */
export function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("gt_install_dismissed") === "1");
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;

  // Only surface on mobile-ish widths (this is the "phone app" affordance).
  const canPrompt = !!deferred;
  const iosEligible = isIos() && !isStandalone();
  if (!canPrompt && !iosEligible) return null;

  const dismiss = () => {
    setDismissed(true);
    setShowIosHelp(false);
    sessionStorage.setItem("gt_install_dismissed", "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else if (iosEligible) {
      setShowIosHelp(true);
    }
  };

  return (
    <div
      data-testid="install-pwa-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md lg:hidden"
    >
      <div className="flex items-center gap-3 rounded-sm border border-border bg-card shadow-lg px-4 py-3">
        <img src="/pwa-192.png" alt="" className="w-9 h-9 rounded-sm shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight">Install Grievance Tracker</div>
          <div className="text-xs text-muted-foreground truncate">Add to your home screen for quick access.</div>
        </div>
        <button
          data-testid="install-pwa-button"
          onClick={install}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-sm bg-primary text-primary-foreground text-xs font-semibold px-3 py-2 hover:bg-primary/90 transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Install
        </button>
        <button
          data-testid="install-pwa-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {showIosHelp && (
        <div className="mt-2 rounded-sm border border-border bg-card shadow-lg px-4 py-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            Tap the <Share className="w-3.5 h-3.5 inline" /> Share button, then
            <span className="font-medium text-foreground">“Add to Home Screen”</span>
            <Plus className="w-3.5 h-3.5 inline" />.
          </div>
        </div>
      )}
    </div>
  );
}
